'use client'

import React, { useRef, useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from 'framer-motion'

interface SparklesProps {
  children: React.ReactNode
  className?: string
  id?: string
  background?: string
  minSize?: number
  maxSize?: number
  quantity?: number
  speed?: number
}

// Helper function to generate a random number between min and max
const randomNumber = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function Sparkles({
  children,
  className,
  id,
  background = 'rgba(255, 255, 255, 0.3)',
  minSize = 1,
  maxSize = 3,
  quantity = 20,
  speed = 1,
}: SparklesProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { once: false, amount: 0.2 })
  const [sparkles, setSparkles] = useState<Array<{
    id: number
    size: number
    left: number
    top: number
    animationDuration: number
  }>>([])

  // Generate initial sparkles
  useEffect(() => {
    if (!isInView || !containerRef.current) return

    const containerWidth = containerRef.current.offsetWidth
    const containerHeight = containerRef.current.offsetHeight

    // Generate sparkles
    const newSparkles = Array.from({ length: quantity }).map((_, i) => ({
      id: i,
      size: randomNumber(minSize, maxSize),
      left: randomNumber(0, 100),
      top: randomNumber(0, 100),
      animationDuration: randomNumber(2, 5) / speed,
    }))

    setSparkles(newSparkles)
  }, [isInView, minSize, maxSize, quantity, speed])

  return (
    <div
      id={id}
      ref={containerRef}
      className={cn('relative overflow-hidden', className)}
    >
      {isInView && sparkles.map((sparkle) => (
        <span
          key={sparkle.id}
          className="animate-twinkle absolute z-10 rounded-full"
          style={{
            width: `${sparkle.size}px`,
            height: `${sparkle.size}px`,
            left: `${sparkle.left}%`,
            top: `${sparkle.top}%`,
            background,
            animationDuration: `${sparkle.animationDuration}s`,
          }}
        />
      ))}
      {children}
    </div>
  )
} 